import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type Router,
  type RouteRecordRaw,
} from "vue-router";
import { authGuard } from "@auth0/auth0-vue";
import { isFeatureEnabled } from "@/utils/featureFlags";

// 環境変数から選考管理機能の表示フラグを取得
const showMatchingFlows = isFeatureEnabled("VITE_SHOW_MATCHING_FLOWS");

// 404エラーページの共通設定
const errorRouteCommon = {
  component: () => import("@/components/shared/error/ErrorView.vue"),
  meta: { layout: "default" },
  props: { statusCode: "404" },
} as const;

// ルート定義を環境変数フラグに応じて動的に生成
const getMatchingFlowsRoutes = (): RouteRecordRaw[] => {
  if (showMatchingFlows) {
    return [
      {
        path: "/matching_flows",
        name: "matchingFlows",
        component: () => import("@/pages/matching-flows/MatchingFlowsPage.vue"),
        meta: { layout: "default" },
      },
      {
        path: "/matching_flows/:id",
        name: "matchingFlowDetail",
        component: () =>
          import("@/pages/matching-flows/[id]/MatchingFlowDetailPage.vue"),
        meta: { layout: "default" },
      },
    ];
  }
  return [
    {
      path: "/matching_flows",
      name: "matchingFlows",
      ...errorRouteCommon,
    },
    {
      path: "/matching_flows/:id",
      name: "matchingFlowDetail",
      ...errorRouteCommon,
    },
  ];
};

export const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "home",
    component: () => import("@/pages/dashboard/DashboardPage.vue"),
    meta: { layout: "default" },
  },
  // 環境変数フラグに応じてルーティングを切り替え
  ...getMatchingFlowsRoutes(),
  {
    path: "/jobs",
    name: "jobs",
    component: () => import("@/pages/jobs/JobsPage.vue"),
    meta: { layout: "default" },
  },
  {
    path: "/jobs/:id",
    name: "jobDetail",
    component: () => import("@/pages/jobs/[id]/JobDetailPage.vue"),
    meta: { layout: "default" },
  },
  {
    path: "/candidates",
    name: "candidates",
    component: () => import("@/pages/candidates/CandidatesPage.vue"),
    meta: { layout: "default" },
  },
  {
    path: "/error",
    name: "error",
    component: () => import("@/components/shared/error/ErrorView.vue"),
    meta: { layout: "default" },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "notFound",
    component: () => import("@/components/shared/error/ErrorView.vue"),
    meta: { layout: "default" },
    props: { statusCode: "404" },
  },
];

export const createAppRouter = (
  history: ReturnType<typeof createWebHistory> = createWebHistory(),
): Router => {
  return createRouter({ history, routes });
};

const router = createAppRouter();

/**
 * Auth0のコールバックURLかどうかを判定
 * @param to ルート情報
 * @returns コールバックURLの場合true
 */
const isAuth0Callback = (to: RouteLocationNormalized): boolean => {
  const query = to.query;
  const code = query.code;
  const state = query.state;
  return Boolean(code && state);
};

router.beforeEach(async (to, _, next) => {
  // エラーページは常に許可
  if (typeof to.name === "string" && ["error", "notFound"].includes(to.name)) {
    return next();
  }

  // Auth0コールバックの場合は認証ガードをスキップ
  if (isAuth0Callback(to)) {
    return next();
  }

  // その他のルートは認証ガードを適用
  try {
    await authGuard(to);
    next();
  } catch {
    next({
      name: "error",
      query: { code: "500" },
    });
  }
});

export default router;
